import { useCallback, useEffect, useRef } from "react";
import { getProgramDeadline } from "../utils/programDuration";

// Browsers overflow larger delays to a near-immediate timeout.
const MAX_TIMEOUT_MS = 2147483647;

export default function useProgramDeadline(program, setActive, setEnded) {
  const timer = useRef(null);
  const clearTimer = useCallback(() => {
    clearTimeout(timer.current);
    timer.current = null;
  }, []);

  const schedule = useCallback((programInfo, starting = false) => {
    clearTimer();
    const deadline = getProgramDeadline(programInfo);
    if (deadline === null) {
      if (starting) setActive(true);
      return;
    }
    const checkDeadline = () => {
      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        setActive(false);
        setEnded(true);
      } else {
        timer.current = setTimeout(checkDeadline, Math.min(remaining, MAX_TIMEOUT_MS));
      }
    };
    // On initial load, the app decides whether past participants need a modal.
    if (deadline > Date.now() || starting) checkDeadline();
    if (starting && deadline > Date.now()) setActive(true);
  }, [clearTimer, setActive, setEnded]);

  const enabled = program?.duration?.enabled;
  const endDateTime = program?.duration?.endDateTime;
  useEffect(() => {
    schedule({ duration: { enabled, endDateTime } });
    return clearTimer;
  }, [enabled, endDateTime, schedule, clearTimer]);

  return useCallback((activeProgram) => schedule(activeProgram, true), [schedule]);
}
